import { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Alert from "@material-ui/lab/Alert";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import { BusinessOwnerService } from "../../../../services/BusinessOwnerService";
import { Overview } from "./components/overview/Overview";
import { Branch } from "./components/branch/Branch";
import { InputButton } from "../../../../components/input-button/InputButton";
import { AddBranch } from "./modals/add_branch/AddBranch";

interface BusinessProps {
  id: number;
}

const useStyles = makeStyles((theme) => ({
  main: {
    width: "100%",
    height: "100%",
    padding: 20,
    flexGrow: 1,
  },
  businessLogo: {
    width: 40,
    height: 40,
    marginRight: 10,
  },
  businessName: {
    fontSize: 32,
    fontWeight: 400,
  },
  tab: {
    textTransform: "none",
  },
  divider: {
    width: "100%",
    height: 2,
    backgroundColor: theme.palette.grey[300],
  },
}));

function a11yProps(index: any) {
  return {
    id: `wrapped-tab-${index}`,
    "aria-controls": `wrapped-tabpanel-${index}`,
  };
}

export const Business = (props: BusinessProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { id } = props;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [business, setBusiness] = useState(null);
  const [branches, setBranches] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [isAddBranchModalOpened, setIsAddBranchModalOpened] = useState(false);

  const loadOverview = async () => {
    setLoading(true);
    setError(false);
    setBusiness(null);
    setBranches([]);
    try {
      const businessOverview = await BusinessOwnerService.getBusinessOverview({
        id,
      });
      if (businessOverview.isSuccess && businessOverview.data.code === 100) {
        const responseData = JSON.parse(
          JSON.stringify(businessOverview.data.data)
        );
        setBusiness(responseData.business);
        setBranches(responseData.branches);
      } else {
        setError(true);
      }
    } catch (_) {
      setError(true);
    }
    setLoading(false);
  };

  const updateScreen = () => {
    loadOverview();
  };

  useEffect(() => {
    loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeTab = (_: React.ChangeEvent<{}>, tab: number) => {
    setActiveTab(tab);
  };

  const toggleAddBranchModal = (succeed: boolean = false) => {
    setIsAddBranchModalOpened(!isAddBranchModalOpened);
    if (succeed) {
      loadOverview();
    }
  };

  return (
    <>
      {isAddBranchModalOpened && (
        <AddBranch
          businessId={id}
          isOpened={isAddBranchModalOpened}
          closeCallback={toggleAddBranchModal}
        />
      )}
      {business === null ? (
        <Paper className={classes.main}>
          <Grid
            className={classes.main}
            container
            justify="center"
            alignItems="center"
            spacing={2}
          >
            {loading && <CircularProgress size={30} />}
            {error && (
              <Alert
                severity="error"
                action={
                  <Button onClick={loadOverview} color="inherit" size="small">
                    <FormattedMessage id="sharedScreen.loadAgain" />
                  </Button>
                }
              >
                <FormattedMessage id="sharedScreen.thereWasAProblemLoadingDatas" />
              </Alert>
            )}
          </Grid>
        </Paper>
      ) : (
        <Paper className={classes.main}>
          <Grid container spacing={2}>
            <Grid item container xs={12} sm={12} lg={12}>
              <Grid item xs={8} sm={8} lg={8} container direction="row">
                <img
                  className={classes.businessLogo}
                  src={business.companyLogo}
                  alt="Company Logo"
                />
                <Typography
                  className={classes.businessName}
                  variant="h4"
                  component="h4"
                >
                  {business.companyName}
                </Typography>
              </Grid>
              <Grid item xs={4} sm={4} lg={4} container justify="flex-end">
                <InputButton
                  onClick={() => toggleAddBranchModal()}
                  color="secondary"
                  variant="contained"
                >
                  + <FormattedMessage id="sharedScreen.addABranch" />
                </InputButton>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} lg={12}>
              <Tabs
                value={activeTab}
                onChange={changeTab}
                indicatorColor="primary"
                textColor="primary"
                aria-label="full width tabs example"
              >
                <Tab
                  className={classes.tab}
                  label={formatMessage({ id: "sharedScreen.overview" })}
                  {...a11yProps(0)}
                />
                {branches.map((branch, index) => (
                  <Tab
                    className={classes.tab}
                    label={branch.name}
                    {...a11yProps(index + 1)}
                  />
                ))}
              </Tabs>
              <div className={classes.divider} />
            </Grid>
            <Grid item xs={12} sm={12} lg={12}>
              <div
                role="tabpanel"
                hidden={activeTab !== 0}
                id="wrapped-tabpanel-0"
                aria-labelledby="wrapped-tab-0"
              >
                {activeTab === 0 && <Overview {...business} callback={updateScreen} />}
              </div>
              {branches.map((branch, index) => (
                <div
                  role="tabpanel"
                  hidden={activeTab !== index + 1}
                  id={`wrapped-tabpanel-${index + 1}`}
                  aria-labelledby={`wrapped-tab-${index + 1}`}
                >
                  {activeTab === index + 1 && (
                    <Branch {...branch} loadOverview={loadOverview} />
                  )}
                </div>
              ))}
            </Grid>
          </Grid>
        </Paper>
      )}
    </>
  );
};
